<!-- Trigger Button -->
<button
  #triggerRef
  class="hx-dropdown__trigger"
  type="button"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-haspopup]="'listbox'"
  [attr.aria-invalid]="error()"
  [attr.aria-required]="required()"
  [attr.aria-readonly]="readonly()"
  [disabled]="disabled() || readonly()"
  (click)="toggleDropdown()"
  (keydown)="onTriggerKeyDown($event)">
  <span class="hx-dropdown__trigger-text">
    <span
      class="hx-dropdown__trigger-label"
      hxEllipsisTooltip
      [hxEllipsisTooltip]="displayText()"
      [ellipsisDisabled]="!showTriggerTooltip()">
      {{ displayText() }}
    </span>
    @if (displaySubtitleText()) {
      <span
        class="hx-dropdown__trigger-subtitle"
        hxEllipsisTooltip
        [hxEllipsisTooltip]="displaySubtitleText()"
        [ellipsisDisabled]="!showTriggerTooltip()">
        {{ displaySubtitleText() }}
      </span>
    }
  </span>

  <!-- Dropdown icon -->
  @if (!hideChevron()) {
    <hx-icon
      [svgIcon]="isOpen() ? 'ccs-chevron-up' : 'ccs-chevron-down'"
      size="16"
      class="designer-canvas__control-chevron"></hx-icon>
  }
</button>

<!-- Dropdown Panel (rendered in overlay) -->
<ng-template #dropdownPortal>
  <div
    tabindex="-1"
    role="listbox"
    data-hx-tab-self-managed
    [id]="panelId"
    [attr.aria-multiselectable]="multiple() ? true : null"
    [attr.aria-activedescendant]="activeOptionDomId()"
    [class]="panelClasses()"
    (keydown)="onPanelKeyDown($event)">
    <!-- Search Input -->
    @if (searchable()) {
      <div class="hx-dropdown-search">
        <hx-input
          type="search"
          [size]="size()"
          startIcon="search"
          placeholder="{{ allowFreeText() ? 'Type any value and press Enter to use...' : 'Search...' }}"
          appDebounce
          [value]="searchQuery()"
          [stopPropagation]="['click', 'keydown']"
          (keydown)="onSearchKeyDown($event)"
          (input)="onSearchInput($event)"></hx-input>
      </div>
    }

    <div
      class="hx-dropdown-panel__options"
      [class.hx-dropdown-panel__options--virtual]="virtualScroll()">
      @if (filteredOptions().length === 0) {
        @if (showFreeTextOption()) {
          <button
            class="hx-dropdown-option hx-dropdown-option--free-text"
            type="button"
            role="option"
            (click)="commitFreeText(searchQuery())">
            <span class="hx-dropdown-no-results__text">
              Type any value and press Enter to use: "{{ searchQuery().trim() }}"
            </span>
          </button>

          <div class="hx-dropdown-divider"></div>
        } @else {
          <!-- No Results Message -->
          <div class="hx-dropdown-no-results">
            <p class="hx-dropdown-no-results__text">No options found</p>
          </div>
        }
      } @else {
        @if (multiple() && showSelectAll()) {
          <button
            class="hx-dropdown-option hx-dropdown-option--select-all"
            type="button"
            [class.hx-dropdown-option--selected]="allSelected()"
            (click)="allSelected() ? deselectAll() : selectAll()">
            <!-- Checkbox for Select All -->
            <hx-checkbox
              aria-hidden="true"
              class="hx-dropdown-option__checkbox"
              [options]="[{ label: '', value: 'select-all' }]"
              [selectedValues]="allSelected() ? ['select-all'] : []"
              [stopPropagation]="['click']"></hx-checkbox>

            <span class="hx-dropdown-option__label">Select All</span>
          </button>

          <div class="hx-dropdown-divider"></div>
        }

        @if (showFreeTextOption()) {
          <button
            class="hx-dropdown-option hx-dropdown-option--free-text"
            type="button"
            role="option"
            (click)="commitFreeText(searchQuery())">
            <span class="hx-dropdown-option__label">Use "{{ searchQuery().trim() }}"</span>
          </button>

          <div class="hx-dropdown-divider"></div>
        }

        @if (virtualScroll()) {
          <!-- Virtual Scroll Mode -->
          <cdk-virtual-scroll-viewport
            [itemSize]="itemSize()"
            class="hx-dropdown-virtual-viewport"
            [style.height.px]="Math.min(displayOptions().length * itemSize(), 300)">
            <div *cdkVirtualFor="let item of displayOptions(); trackBy: trackByFn">
              @if (isOptionGroup(item)) {
                <!-- Option Group -->
                <div class="hx-dropdown-option-group">
                  <div class="hx-dropdown-option-group__label">
                    {{ item.label }}
                  </div>
                  @for (option of item.options; track option.id; let idx = $index) {
                    <button
                      type="button"
                      role="option"
                      tabindex="-1"
                      [id]="optionDomId(option)"
                      [attr.aria-disabled]="option.disabled"
                      [attr.aria-selected]="isOptionSelected(option)"
                      [class]="getOptionClasses(option, idx)"
                      [disabled]="option.disabled || false"
                      (click)="selectOption(option)">
                      <!-- Checkbox for multi-select -->
                      @if (multiple()) {
                        <hx-checkbox
                          aria-hidden="true"
                          class="hx-dropdown-option__checkbox"
                          [options]="[{ label: '', value: option.id }]"
                          [selectedValues]="isOptionSelected(option) ? [option.id] : []"
                          (click)="$event.stopPropagation()"></hx-checkbox>
                      }

                      @if (option.icon) {
                        <span class="hx-dropdown-option__icon">
                          {{ option.icon }}
                        </span>
                      }

                      <span class="hx-dropdown-option__label-container">
                        <span
                          class="hx-dropdown-option__label"
                          [attr.title]="option.title ?? option.label">
                          {{ option.label }}
                        </span>
                        @if (option.subtitle) {
                          <span
                            class="hx-dropdown-option__subtitle"
                            [attr.title]="option.subtitle">
                            {{ option.subtitle }}
                          </span>
                        }
                      </span>

                      <!-- Checkmark for single-select -->
                      @if (!multiple() && isOptionSelected(option)) {
                        <hx-icon
                          aria-hidden="true"
                          class="hx-dropdown-option__check"
                          svgIcon="ccs-check"
                          size="16"></hx-icon>
                      }
                    </button>
                  }
                </div>
              } @else {
                <!-- Single Option -->
                @for (option of [item]; track option.id; let idx = $index) {
                  <button
                    type="button"
                    role="option"
                    tabindex="-1"
                    [id]="optionDomId(asOption(option))"
                    [attr.aria-disabled]="asOption(option).disabled"
                    [attr.aria-selected]="isOptionSelected(asOption(option))"
                    [class]="getOptionClasses(asOption(option), idx)"
                    [disabled]="asOption(option).disabled || false"
                    (click)="selectOption(asOption(option))">
                    <!-- Checkbox for multi-select -->
                    @if (multiple()) {
                      <hx-checkbox
                        aria-hidden="true"
                        class="hx-dropdown-option__checkbox"
                        [options]="[{ label: '', value: asOption(option).id }]"
                        [selectedValues]="isOptionSelected(asOption(option)) ? [asOption(option).id] : []"
                        (click)="$event.stopPropagation()"></hx-checkbox>
                    }

                    @if (asOption(option).icon) {
                      <span class="hx-dropdown-option__icon">
                        {{ asOption(option).icon }}
                      </span>
                    }

                    <span class="hx-dropdown-option__label-container">
                      <span
                        class="hx-dropdown-option__label"
                        [attr.title]="asOption(option).title ?? asOption(option).label">
                        {{ asOption(option).label }}
                      </span>
                      @if (asOption(option).subtitle) {
                        <span
                          class="hx-dropdown-option__subtitle"
                          [attr.title]="asOption(option).subtitle">
                          {{ asOption(option).subtitle }}
                        </span>
                      }
                    </span>

                    <!-- Checkmark for single-select -->
                    @if (!multiple() && isOptionSelected(asOption(option))) {
                      <hx-icon
                        aria-hidden="true"
                        class="hx-dropdown-option__check"
                        svgIcon="ccs-check"
                        size="16"></hx-icon>
                    }
                  </button>
                }
              }
            </div>
          </cdk-virtual-scroll-viewport>
        } @else {
          <!-- Regular Mode -->
          @for (item of displayOptions(); track item.id) {
            @if (isOptionGroup(item)) {
              <!-- Option Group -->
              <div class="hx-dropdown-option-group">
                <div class="hx-dropdown-option-group__label">
                  {{ item.label }}
                </div>
                @for (option of item.options; track option.id; let idx = $index) {
                  <button
                    type="button"
                    role="option"
                    tabindex="-1"
                    [id]="optionDomId(option)"
                    [attr.aria-disabled]="option.disabled"
                    [attr.aria-selected]="isOptionSelected(option)"
                    [class]="getOptionClasses(option, idx)"
                    [disabled]="option.disabled || false"
                    (click)="selectOption(option)">
                    @if (multiple()) {
                      <hx-checkbox
                        aria-hidden="true"
                        class="hx-dropdown-option__checkbox"
                        [options]="[{ label: '', value: option.id }]"
                        [selectedValues]="isOptionSelected(option) ? [option.id] : []"
                        (click)="$event.stopPropagation()"></hx-checkbox>
                    }

                    @if (option.icon) {
                      <span class="hx-dropdown-option__icon">
                        {{ option.icon }}
                      </span>
                    }

                    <span class="hx-dropdown-option__label-container">
                      <span
                        class="hx-dropdown-option__label"
                        [attr.title]="option.title ?? option.label">
                        {{ option.label }}
                      </span>
                      @if (option.subtitle) {
                        <span
                          class="hx-dropdown-option__subtitle"
                          [attr.title]="option.subtitle">
                          {{ option.subtitle }}
                        </span>
                      }
                    </span>

                    @if (!multiple() && isOptionSelected(option)) {
                      <hx-icon
                        aria-hidden="true"
                        class="hx-dropdown-option__check"
                        svgIcon="ccs-check"
                        size="16"></hx-icon>
                    }
                  </button>
                }
              </div>
            } @else {
              <!-- Single Option -->
              @for (option of [item]; track option.id; let idx = $index) {
                <button
                  type="button"
                  role="option"
                  tabindex="-1"
                  [id]="optionDomId(asOption(option))"
                  [attr.aria-disabled]="asOption(option).disabled"
                  [attr.aria-selected]="isOptionSelected(asOption(option))"
                  [class]="getOptionClasses(asOption(option), idx)"
                  [disabled]="asOption(option).disabled || false"
                  (click)="selectOption(asOption(option))">
                  @if (multiple()) {
                    <hx-checkbox
                      aria-hidden="true"
                      class="hx-dropdown-option__checkbox"
                      [options]="[{ label: '', value: asOption(option).id }]"
                      [selectedValues]="isOptionSelected(asOption(option)) ? [asOption(option).id] : []"
                      (click)="$event.stopPropagation()"></hx-checkbox>
                  }

                  @if (asOption(option).icon) {
                    <span class="hx-dropdown-option__icon">
                      {{ asOption(option).icon }}
                    </span>
                  }

                  <span class="hx-dropdown-option__label-container">
                    <span
                      class="hx-dropdown-option__label"
                      [attr.title]="asOption(option).title ?? asOption(option).label">
                      {{ asOption(option).label }}
                    </span>
                    @if (asOption(option).subtitle) {
                      <span
                        class="hx-dropdown-option__subtitle"
                        [attr.title]="asOption(option).subtitle">
                        {{ asOption(option).subtitle }}
                      </span>
                    }
                  </span>

                  @if (!multiple() && isOptionSelected(asOption(option))) {
                    <hx-icon
                      aria-hidden="true"
                      class="hx-dropdown-option__check"
                      svgIcon="ccs-check"
                      size="16"></hx-icon>
                  }
                </button>
              }
            }
          }
        }
      }
    </div>
  </div>
</ng-template>
